import DirectionsResult = google.maps.DirectionsResult;
import cloneDeep from "lodash/cloneDeep";
// Local
import { ResourceTechnicianVehicle } from "../../../Pages/Scheduling/SchedulingDetailContainer";
import { ExistingSiteKeyUserPermissions } from "../../../models/site-key-user-permissions";
import { ExistingSiteKeyLocation } from "../../../models/site-key-location";
import { ExistingTask } from "../../../models/task";
import { logger } from "../../../logging";

interface TaskStop {
  task: ExistingTask;
  latLng: google.maps.LatLngLiteral;
}

export default async function getDirections({
  resources,
  siteKeyLocations,
  permissionsMap,
  mapCenter,
}: {
  resources: ResourceTechnicianVehicle[];
  siteKeyLocations: ExistingSiteKeyLocation[];
  permissionsMap: Record<string, ExistingSiteKeyUserPermissions>;
  mapCenter: { lat: number; lng: number };
}): Promise<Record<string, DirectionsResult>> {
  const filteredResources: ResourceTechnicianVehicle[] = [];
  for (const r of resources) {
    if (r.taskList.length > 0) filteredResources.push(r);
  }
  if (filteredResources.length === 0) return {};
  logger.info(
    `running getDirections for resources that have tasks (${filteredResources.length})`,
  );

  const directionsService = new google.maps.DirectionsService();
  const directionsResponsesTmp: Record<string, DirectionsResult> = {};

  // Generate promises for all resources
  const directionsServicePromises = filteredResources.map(async (resource) => {
    const origin = getOriginForResource({
      resource,
      siteKeyLocations: siteKeyLocations,
      permissionsMap: permissionsMap,
      mapCenter,
    });

    const stops: TaskStop[] = resource.taskList.flatMap((task) => {
      if (
        typeof task.latitude !== "number" ||
        typeof task.longitude !== "number"
      ) {
        logger.error(
          `${task.id} - Task missing lat/lng. lat: ${task.latitude} lng: ${task.longitude}`,
        );
        return [];
      }
      return {
        task: { ...task },
        latLng: { lat: task.latitude, lng: task.longitude },
      };
    });

    const resourceID =
      resource.vehicle?.id ?? resource.technicians[0]?.id ?? "unknown";

    const result = await getDirectionsFn(directionsService, {
      origin,
      destination: origin,
      waypoints: getWaypoints(stops),
      // @ts-ignore
      travelMode: "DRIVING",
    });
    return { resourceID, result };
  });

  const promiseResults = await Promise.all(directionsServicePromises);

  // Populate the directions response object with results
  for (const { resourceID, result } of promiseResults) {
    // console.log(`resourceID: ${resourceID} result:`, result);
    directionsResponsesTmp[resourceID] = result;
  }
  return directionsResponsesTmp;
}

function getWaypoints(stops: TaskStop[]): google.maps.DirectionsWaypoint[] {
  const waypts: google.maps.DirectionsWaypoint[] = [];
  for (const stop of stops) {
    waypts.push({ location: stop.latLng, stopover: true });
  }
  return waypts;
}

/**
 * This function returns the position (lat/long coords) for a resource.
 * - It first checks if the resource has a vehicle, and if so, it returns the vehicle's latitude and longitude.
 * - If the resource does not have a vehicle, it checks if the technician has a defaultLocationID, and if so, it returns the defaultLocationID's latitude and longitude.
 * - If the resource does not have a vehicle or a defaultLocationID, it returns the mapCenter.
 */
export function getOriginForResource(args: {
  resource: ResourceTechnicianVehicle;
  siteKeyLocations: ExistingSiteKeyLocation[];
  permissionsMap: Record<string, ExistingSiteKeyUserPermissions>;
  mapCenter: { lat: number; lng: number };
}): { lat: number; lng: number } {
  const { resource, siteKeyLocations, permissionsMap, mapCenter } = args;

  if (resource.vehicle) {
    return {
      lat: resource.vehicle.latitude,
      lng: resource.vehicle.longitude,
    };
  }

  // If the resource is a technician, we need to find their default location
  const technician = resource.technicians[0];

  // first check if the technician has a defaultLocationID
  const permissions = permissionsMap[technician.id];
  const technicianLocation = siteKeyLocations.find(
    (location) => location.id === permissions?.defaultLocationID,
  );
  if (technicianLocation) {
    return {
      lat: technicianLocation.latitude,
      lng: technicianLocation.longitude,
    };
  }

  // If the technician does not have a defaultLocationID, use the first task's siteKeyLocation lat/long
  if (resource.taskList.length > 0) {
    const clonedTaskList = cloneDeep(resource.taskList);
    const sortedTasks = clonedTaskList.sort((a, b) => {
      if (a.timestampScheduled === null) return -1;
      if (b.timestampScheduled === null) return 1;
      return a.timestampScheduled > b.timestampScheduled ? -1 : 1;
    });
    const firstTask = sortedTasks[0];
    if (firstTask.locationID) {
      const locationDoc = siteKeyLocations.find(
        (location) => location.id === firstTask.locationID,
      );
      if (locationDoc) {
        return {
          lat: locationDoc.latitude,
          lng: locationDoc.longitude,
        };
      }
    }
  }

  // Fallback to mapCenter (which should be siteKey lat/lon)
  return mapCenter;
}

// Helper function to wrap the directionsService.route method in a promise
function getDirectionsFn(
  directionsService: google.maps.DirectionsService,
  options: google.maps.DirectionsRequest,
) {
  return new Promise<google.maps.DirectionsResult>((resolve, reject) => {
    directionsService.route(options, (response, status) => {
      if (status === "OK" && response !== null) {
        resolve(response);
      } else {
        reject(new Error(`Directions request failed: ${status}`));
      }
    });
  });
}
